import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  Box,
} from '@mui/material';
import { ImageField, useNotify } from 'react-admin';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { postJson } from '../../utils/api';

const ALL_GIFTCARDS = 200;

type GiftCardOrderingDialogProps = {
  open: boolean;
  onClose: () => void;
  refresh: Function;
};

type GiftCard = {
  id: string;
  name: string;
  order: number;
  assets: {
    logoUrl: string;
    giftCardUrl: string;
  };
};

type GiftCardSearchResponse = {
  giftCards: GiftCard[];
};

const DraggableGiftCard: React.FC<DraggableGiftCardProps> = ({
  index,
  giftCard,
  moveGiftCard,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: 'giftCard',
    hover(item: { index: number }, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveGiftCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'giftCard',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <Grid item xs={12} sm={12} md={6} lg={4}>
      <div
        ref={ref}
        style={{
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '10px',
          textAlign: 'center',
          cursor: 'move',
          opacity: isDragging ? 0.5 : 1,
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <ImageField
          source="assets.giftCardUrl"
          record={giftCard}
          label="Gift Card"
          sx={{ maxWidth: '100%' }}
        />
        <p>{giftCard.name}</p>
      </div>
    </Grid>
  );
};

export const GiftCardOrderingDialog: React.FC<GiftCardOrderingDialogProps> = ({
  open,
  onClose,
  refresh,
}) => {
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [initialGiftCards, setInitialGiftCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const notify = useNotify();

  const fetchGiftCards = async () => {
    setLoading(true);
    try {
      const response = await postJson(
        `/admin/gift-card/search?limit=${ALL_GIFTCARDS}`,
        {},
      );
      const { data } = await response.json();
      const { giftCards: fetchedGiftCards } = data as GiftCardSearchResponse;

      const sortedGiftCards = fetchedGiftCards.sort(
        (a, b) => a.order - b.order,
      );
      setGiftCards(sortedGiftCards);
      setInitialGiftCards(sortedGiftCards);
    } catch (error) {
      console.error('Failed to fetch gift cards:', error);
      notify('Failed to fetch gift cards', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchGiftCards();
    }
  }, [open]);

  const moveGiftCard = (dragIndex: number, hoverIndex: number) => {
    const draggedGiftCard = giftCards[dragIndex];
    const updatedGiftCards = [...giftCards];
    updatedGiftCards.splice(dragIndex, 1);
    updatedGiftCards.splice(hoverIndex, 0, draggedGiftCard);
    updatedGiftCards.forEach((giftCard, index) => {
      giftCard.order = index;
    });
    setGiftCards(updatedGiftCards);
  };

  const handleSaveOrder = async () => {
    const changedGiftCards = giftCards.filter((giftCard, index) => {
      return giftCard.order !== initialGiftCards[index].order;
    });

    if (changedGiftCards.length === 0) {
      notify('No changes to save', { type: 'info' });
      return;
    }

    const orderPayload = changedGiftCards.map((giftCard, index) => ({
      id: giftCard.id,
      order: index + 1,
    }));

    try {
      await postJson('/admin/gift-card/order', { giftCards: orderPayload });
      notify('Gift card order saved successfully', { type: 'success' });
      setInitialGiftCards([...giftCards]); // Update initial order after save
    } catch (error) {
      notify('Failed to save gift card order', { type: 'error' });
      console.error('Error saving gift card order:', error);
    } finally {
      refresh();
    }
  };

  const handleClose = () => {
    setGiftCards([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Reorder Gift Cards</DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        <DndProvider backend={HTML5Backend}>
          <Grid container spacing={2} sx={{ overflow: 'hidden' }}>
            {giftCards.map((giftCard, index) => (
              <DraggableGiftCard
                key={giftCard.id}
                index={index}
                giftCard={giftCard}
                moveGiftCard={moveGiftCard}
              />
            ))}
          </Grid>
        </DndProvider>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleSaveOrder}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          Save Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type DraggableGiftCardProps = {
  index: number;
  giftCard: GiftCard;
  moveGiftCard: (dragIndex: number, hoverIndex: number) => void;
};
